import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { FiArrowLeft, FiSave, FiLock, FiAlertCircle } from 'react-icons/fi';
import { ARCHERY_CLUBS } from '../../constants/ArcheryClubs';

const EditProfile = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Form adatok
    const [formData, setFormData] = useState({
        lastName: '',
        firstName: '',
        club: '',
        license: '',
        birthDate: '' // ÚJ
    });

    // Korlátozás adatok
    const [nameChangeRestricted, setNameChangeRestricted] = useState(false);
    const [daysLeft, setDaysLeft] = useState(0);
    const [originalName, setOriginalName] = useState({ last: '', first: '' });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);

        const fetchUserData = async () => {
            const user = auth.currentUser;
            if (!user) {
                navigate('/');
                return;
            }

            try {
                const userDocRef = doc(db, 'users', user.uid);
                const userSnap = await getDoc(userDocRef);

                if (userSnap.exists()) {
                    const data = userSnap.data();
                    const lastName = data.lastName || '';
                    const firstName = data.firstName || '';

                    // Születési dátum konvertálása YYYY-MM-DD formátumra az inputhoz
                    let bDateStr = '';
                    if (data.birthDate) {
                        const d = new Date(data.birthDate);
                        bDateStr = d.toISOString().split('T')[0];
                    }

                    setFormData({
                        lastName: lastName,
                        firstName: firstName,
                        club: data.club || 'Egyéb',
                        license: data.license || '',
                        birthDate: bDateStr
                    });

                    setOriginalName({ last: lastName, first: firstName });

                    // Korlátozás ellenőrzése (30 nap)
                    const lastUpdate = data.lastNameChangeTimestamp || 0;
                    const currentTime = Date.now();
                    const thirtyDaysInMillis = 30 * 24 * 60 * 60 * 1000;

                    if (currentTime - lastUpdate < thirtyDaysInMillis && lastUpdate !== 0) {
                        const diff = thirtyDaysInMillis - (currentTime - lastUpdate);
                        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
                        setDaysLeft(days);
                        setNameChangeRestricted(true);
                    }
                }
            } catch (err) {
                console.error("Hiba az adatok betöltésekor:", err);
                setError('Nem sikerült betölteni a profiladatokat.');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
        return () => window.removeEventListener('resize', handleResize);
    }, [navigate]);

    const handleSave = async () => {
        if (!formData.lastName || !formData.firstName) {
            setError('A vezetéknév és keresztnév megadása kötelező!');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const user = auth.currentUser;
            const userDocRef = doc(db, 'users', user.uid);

            const updates = {
                lastName: formData.lastName,
                firstName: formData.firstName,
                displayName: `${formData.lastName} ${formData.firstName}`.trim(),
                club: formData.club,
                license: formData.license,
                birthDate: formData.birthDate ? new Date(formData.birthDate).getTime() : 0
            };

            // Ha változott a név, frissítjük az időbélyeget
            if (formData.lastName !== originalName.last || formData.firstName !== originalName.first) {
                updates.lastNameChangeTimestamp = Date.now();
            }

            await updateDoc(userDocRef, updates);
            setSuccess(true);
            setTimeout(() => navigate('/profil'), 1500);
        } catch (err) {
            console.error("Hiba a mentéskor:", err);
            setError('Hiba történt a mentés során. Próbáld újra később!');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={containerStyle}><p style={{textAlign: 'center'}}>Betöltés... 🏹</p></div>;

    return (
        <div className="force-light-theme" style={containerStyle}>
            <div style={headerStyle}>
                <button style={backButtonStyle} onClick={() => navigate('/profil')}>
                    <FiArrowLeft size={24} />
                </button>
                <h1 style={titleStyle}>Profil szerkesztése</h1>
            </div>

            <div style={cardStyle}>
                {/* VEZETÉKNÉV */}
                <div style={inputGroupStyle}>
                    <label style={labelStyle}>Vezetéknév</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            disabled={nameChangeRestricted}
                            style={{ ...inputStyle, borderColor: nameChangeRestricted ? '#ffa39e' : '#ddd', backgroundColor: nameChangeRestricted ? '#fff2f0' : '#fff' }}
                        />
                        {nameChangeRestricted && <FiLock style={lockIconStyle} />}
                    </div>
                </div>

                {/* KERESZTNÉV */}
                <div style={inputGroupStyle}>
                    <label style={labelStyle}>Keresztnév</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            disabled={nameChangeRestricted}
                            style={{ ...inputStyle, borderColor: nameChangeRestricted ? '#ffa39e' : '#ddd', backgroundColor: nameChangeRestricted ? '#fff2f0' : '#fff' }}
                        />
                        {nameChangeRestricted && <FiLock style={lockIconStyle} />}
                    </div>
                </div>

                {/* NÉVMÓDOSÍTÁS KORLÁTOZÁS ÜZENET */}
                {nameChangeRestricted && (
                    <div style={restrictionBoxStyle}>
                        <FiAlertCircle size={18} />
                        <span>A nevedet legközelebb <strong>{daysLeft} nap múlva</strong> módosíthatod.</span>
                    </div>
                )}

                {/* EGYESÜLET */}
                <div style={inputGroupStyle}>
                    <label style={labelStyle}>Egyesület</label>
                    <select
                        value={formData.club}
                        onChange={(e) => setFormData({ ...formData, club: e.target.value })}
                        style={inputStyle}
                    >
                        {ARCHERY_CLUBS.map(club => (
                            <option key={club} value={club}>{club}</option>
                        ))}
                    </select>
                </div>

                {/* VERSENYENGEDÉLY */}
                <div style={inputGroupStyle}>
                    <label style={labelStyle}>Versenyengedély száma (opcionális)</label>
                    <input
                        type="text"
                        placeholder="Pl. 12345"
                        value={formData.license}
                        onChange={(e) => setFormData({ ...formData, license: e.target.value })}
                        style={inputStyle}
                    />
                    <small style={helperTextStyle}>Ez az adat nem publikus, csak a saját profilodon látható.</small>
                </div>

                {/* SZÜLETÉSI DÁTUM */}
                <div style={inputGroupStyle}>
                    <label style={labelStyle}>Születési dátum</label>
                    <input
                        type="date"
                        value={formData.birthDate}
                        onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                        style={inputStyle}
                    />
                    <small style={helperTextStyle}>Ez az adat nem publikus, csak a saját profilodon látható.</small>
                </div>

                {error && <div style={errorStyle}>{error}</div>}
                {success && <div style={successStyle}>Profil sikeresen elmentve! Átirányítás...</div>}

                <button
                    onClick={handleSave}
                    disabled={saving || success}
                    style={{ ...saveButtonStyle, opacity: (saving || success) ? 0.7 : 1 }}
                >
                    {saving ? 'Mentés...' : <><FiSave style={{marginRight: '8px'}} /> Mentés</>}
                </button>
            </div>
        </div>
    );
};

// --- STÍLUSOK ---
const containerStyle = {
    padding: '1.5rem',
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
    minHeight: '100vh',
    backgroundColor: '#f8f9fa'
};

const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '2rem',
    marginTop: '1rem'
};

const backButtonStyle = {
    background: 'none',
    border: 'none',
    color: '#333',
    cursor: 'pointer',
    padding: '5px',
    display: 'flex',
    alignItems: 'center'
};

const titleStyle = {
    fontSize: '1.5rem',
    margin: 0,
    color: '#333',
    fontWeight: 'bold'
};

const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '2rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
};

const inputGroupStyle = {
    marginBottom: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
};

const labelStyle = {
    fontSize: '0.85rem',
    fontWeight: 'bold',
    color: '#b30000',
    textTransform: 'uppercase'
};

const inputStyle = {
    width: '100%',
    padding: '12px 15px',
    borderRadius: '10px',
    border: '1px solid #ddd',
    fontSize: '1rem',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s'
};

const lockIconStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#b30000'
};

const restrictionBoxStyle = {
    backgroundColor: '#fff2f0',
    border: '1px solid #ffccc7',
    color: '#cf1322',
    padding: '10px 15px',
    borderRadius: '8px',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '1.5rem'
};

const helperTextStyle = {
    fontSize: '0.75rem',
    color: '#888',
    marginTop: '4px'
};

const saveButtonStyle = {
    width: '100%',
    backgroundColor: '#D32F2F',
    color: '#fff',
    border: 'none',
    padding: '14px',
    borderRadius: '10px',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '1rem',
    boxShadow: '0 4px 10px rgba(211, 47, 47, 0.2)'
};

const errorStyle = {
    color: '#dc3545',
    backgroundColor: '#f8d7da',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '1rem',
    textAlign: 'center',
    fontSize: '0.9rem'
};

const successStyle = {
    color: '#28a745',
    backgroundColor: '#e6fffa',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '1rem',
    textAlign: 'center',
    fontSize: '0.9rem',
    fontWeight: 'bold'
};

export default EditProfile;
